import { useMemo, useState } from "react"

import moment from "moment-timezone"
import { useForm } from "react-hook-form"

import { AvailablePlanType } from "@common-shared/config/coverage.config"
import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import {
    IOneVerifyCoverageOverride,
    IOneVerifyCoverageResult,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { GENERAL_CONFIG } from "../config/general.config"
import { OVERRIDES_CONFIG } from "../config/overrides.config"
import { RuntimeHelper } from "../helpers/runtime.helper"
import { UtilHelper } from "../helpers/util.helper"
import { useAppSelector } from "../store/selectors/app.selector"
import { runtimeSelector } from "../store/selectors/runtime.selector"
import type {
    ICoverageOverrideFormData,
    IOverrideDefaultData,
} from "../types/data.types"
import { useIsFeatureFlagEnabled } from "./isFeatureFlagEnabled.hook"

export const useBulkOverrides = () => {
    const isOverridesEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_OVERRIDES,
    )

    const overridesFormMethods = useForm()
    const dataWatcher: ICoverageOverrideFormData = overridesFormMethods.watch()

    const runtimeState = useAppSelector(runtimeSelector)

    const [suggestedOverrideExpirationDate] = useState(moment().endOf("year"))

    // Minimum expiration date for override is 1 day from now
    // If in UTC timezone "now" is already tomorrow, then it will be 2 days from now so in UTC it's always at least 1 day from now
    const [minOverrideExpirationDate] = useState(() => {
        const localDate = moment().format(GENERAL_CONFIG.mysqlMomentDateFormat)

        const utcDate = moment()
            .utc()
            .format(GENERAL_CONFIG.mysqlMomentDateFormat)

        const isBehindUTC = moment().utcOffset() < 0
        const needsExtraDay = isBehindUTC && localDate !== utcDate

        const offsetDays = needsExtraDay ? 2 : 1

        return moment().add(offsetDays, "days")
    })

    const [overridesFormKey, setOverridesFormKey] = useState<number>(
        new Date().getTime(),
    )

    const isCreateOverrideLoading = useMemo(() => {
        return RuntimeHelper.isCoverageOverridesCreationLoading()
    }, [runtimeState.isLoading])

    const onUseSuggestedOverrideExpiration = () => {
        overridesFormMethods.setValue(
            "expirationDate",
            suggestedOverrideExpirationDate,
        )
    }

    const getFormDefaultData = (
        policyData:
            | IOneVerifyCoverageOverride
            | IOneVerifyCoverageResult
            | null,
        policyDataSecondary?:
            | IOneVerifyCoverageOverride
            | IOneVerifyCoverageResult,
        fallbackDefaultValues = true,
    ): ICoverageOverrideFormData => {
        function normalizeTPA(
            data?: IOneVerifyCoverageOverride | IOneVerifyCoverageResult | null,
        ) {
            if (!data) {
                return {
                    name: undefined,
                    type: undefined,
                }
            }

            return {
                name:
                    "thirdPartyAdministrator" in data
                        ? data.thirdPartyAdministrator?.name
                        : data.thirdPartyAdministratorName,
                type:
                    "thirdPartyAdministrator" in data
                        ? data.thirdPartyAdministrator?.type
                        : data.thirdPartyAdministratorType,
            }
        }

        const benefitStructure =
            policyData?.benefitStructure ||
            policyDataSecondary?.benefitStructure

        // Find current benefit structure and get fields that should be set to null since they are not applicable
        const notApplicableFields = benefitStructure
            ? OVERRIDES_CONFIG.benefitsStructuresConfig.find(
                  item => item.title === benefitStructure,
              )?.notApplicableOnBeingExistingResponseFields
            : undefined

        const modalityCoverageStatus =
            policyData?.modalityCoverageStatus ||
            policyDataSecondary?.modalityCoverageStatus

        const eligibilityBeginDate =
            policyData?.eligibilityBeginDate ||
            policyDataSecondary?.eligibilityBeginDate

        const eligibilityEndDate =
            policyData?.eligibilityEndDate ||
            policyDataSecondary?.eligibilityEndDate

        let coinsurance = UtilHelper.getNumericValueIfSet(
            policyData?.coinsurance,
            policyDataSecondary?.coinsurance,
        )

        // Don't null fields that are 0 below, so they are still shown in UI
        // That's why checking if !== 0

        coinsurance =
            notApplicableFields?.includes("coinsurance") && coinsurance !== 0
                ? null
                : coinsurance

        let copayment = UtilHelper.getNumericValueIfSet(
            policyData?.copayment,
            policyDataSecondary?.copayment,
        )

        copayment =
            notApplicableFields?.includes("copayment") && copayment !== 0
                ? null
                : copayment

        let deductible = UtilHelper.getNumericValueIfSet(
            policyData?.deductible,
            policyDataSecondary?.deductible,
        )

        deductible =
            notApplicableFields?.includes("deductible") && deductible !== 0
                ? null
                : deductible

        let remainingDeductible = UtilHelper.getNumericValueIfSet(
            policyData?.remainingDeductible,
            policyDataSecondary?.remainingDeductible,
        )

        remainingDeductible =
            notApplicableFields?.includes("remainingDeductible") &&
            remainingDeductible !== 0
                ? null
                : remainingDeductible

        let oopMax = UtilHelper.getNumericValueIfSet(
            policyData?.oopMax,
            policyDataSecondary?.oopMax,
        )

        oopMax =
            notApplicableFields?.includes("oopMax") && oopMax !== 0
                ? null
                : oopMax

        let remainingOopMax = UtilHelper.getNumericValueIfSet(
            policyData?.remainingOopMax,
            policyDataSecondary?.remainingOopMax,
        )

        remainingOopMax =
            notApplicableFields?.includes("remainingOopMax") &&
            remainingOopMax !== 0
                ? null
                : remainingOopMax

        const planStatus =
            policyData?.planStatus || policyDataSecondary?.planStatus

        const planType = policyData?.planType || policyDataSecondary?.planType

        const planBeginDate =
            policyData?.planBeginDate || policyDataSecondary?.planBeginDate

        const planEndDate =
            policyData?.planEndDate || policyDataSecondary?.planEndDate

        const thirdPartyAdministratorName =
            normalizeTPA(policyData)?.name ||
            normalizeTPA(policyDataSecondary)?.name ||
            undefined
        const thirdPartyAdministratorType =
            normalizeTPA(policyData)?.type ||
            normalizeTPA(policyDataSecondary)?.type ||
            undefined

        const memberObligation = UtilHelper.getNumericValueIfSet(
            policyData?.memberObligation,
            policyDataSecondary?.memberObligation,
        )

        const payerObligation = UtilHelper.getNumericValueIfSet(
            policyData?.payerObligation,
            policyDataSecondary?.payerObligation,
        )

        const result: ICoverageOverrideFormData = {
            modalityCoverageStatus: modalityCoverageStatus || undefined,
            eligibilityBeginDate: eligibilityBeginDate
                ? moment(eligibilityBeginDate)
                : undefined,
            eligibilityEndDate: eligibilityEndDate
                ? moment(eligibilityEndDate)
                : undefined,
            coinsurance:
                coinsurance !== null && typeof coinsurance !== "undefined"
                    ? coinsurance
                    : undefined,
            copayment:
                copayment !== null && typeof copayment !== "undefined"
                    ? UtilHelper.formatCents(copayment)
                    : undefined,
            deductible:
                deductible !== null && typeof deductible !== "undefined"
                    ? UtilHelper.formatCents(deductible)
                    : undefined,
            remainingDeductible:
                remainingDeductible !== null &&
                typeof remainingDeductible !== "undefined"
                    ? UtilHelper.formatCents(remainingDeductible)
                    : undefined,
            oopMax:
                oopMax !== null && typeof oopMax !== "undefined"
                    ? UtilHelper.formatCents(oopMax)
                    : undefined,
            remainingOopMax:
                remainingOopMax !== null &&
                typeof remainingOopMax !== "undefined"
                    ? UtilHelper.formatCents(remainingOopMax)
                    : undefined,
            planStatus: planStatus !== null ? planStatus : undefined,
            planType:
                planType ||
                (fallbackDefaultValues
                    ? AvailablePlanType.NOT_APPLICABLE
                    : undefined),
            planBeginDate: planBeginDate ? moment(planBeginDate) : undefined,
            planEndDate: planEndDate ? moment(planEndDate) : undefined,
            benefitStructure: undefined,
            customBenefitStructure: undefined,
            memberObligation:
                memberObligation !== null &&
                typeof memberObligation !== "undefined"
                    ? UtilHelper.formatCents(memberObligation)
                    : undefined,
            payerObligation:
                payerObligation !== null &&
                typeof payerObligation !== "undefined"
                    ? UtilHelper.formatCents(payerObligation)
                    : undefined,
            thirdPartyAdministratorName,
            thirdPartyAdministratorType,
        }

        if (benefitStructure) {
            const knownBenefits = OVERRIDES_CONFIG.benefitsStructuresConfig.map(
                item => item.title,
            )

            result.benefitStructure = knownBenefits.includes(benefitStructure)
                ? benefitStructure
                : OVERRIDES_CONFIG.defaultBenefitsStructure

            // If benefit structure is unknown - set it to "custom" for Other option
            result.customBenefitStructure =
                result.benefitStructure ===
                    OVERRIDES_CONFIG.defaultBenefitsStructure &&
                benefitStructure !== OVERRIDES_CONFIG.defaultBenefitsStructure
                    ? benefitStructure
                    : undefined
        }

        return result
    }

    const onResetOverride = () => {
        overridesFormMethods.reset()
        setOverridesFormKey(new Date().getTime())
    }

    const validateOverrideCreation = (
        data: ICoverageOverrideFormData,
    ): boolean => {
        let errorMessage: string | undefined

        // Make sure that "different" data has set values for more than 1 item
        // This single item that we ignore is expirationDate
        if (Object.keys(data).length < 2) {
            errorMessage = OVERRIDES_CONFIG.validations.noDataProvided
        }

        // If benefit structure is other -> and description the one from known values -> don't allow it
        if (
            data.customBenefitStructure &&
            OVERRIDES_CONFIG.benefitsStructuresConfig
                .map(item => item.title)
                .includes(data.customBenefitStructure)
        ) {
            errorMessage =
                OVERRIDES_CONFIG.validations.otherBenefitStructureEqualKnown
        }

        // if benefits structure is not Other, don't allow to override obligations
        if (
            !data.customBenefitStructure &&
            (typeof data.memberObligation === "number" ||
                typeof data.payerObligation === "number")
        ) {
            errorMessage = OVERRIDES_CONFIG.validations.memberPayerObligations
        }

        if (!errorMessage) {
            return true
        }

        ToastrHelper.error(errorMessage)

        return false
    }

    /*
     * This function selects the fields which will be sent to the server as overrides.
     *
     * When we send overrides to the server, we always send all the fields, set to either null or their override value.
     * If we send a value for a field, it will be considered an override even if it's the same as the programmatic value.
     * If we send null for a field, the value gets reset to the programmatic value (not necessarily to null).
     * As a result, there is no such thing as setting an existing programmatic value to null via override.
     *
     * Undefined values will be converted to null elsewhere prior to sending.
     *
     */
    const mapCreationFormData = (
        data: any,
        defaultData: IOverrideDefaultData,
    ): ICoverageOverrideFormData => {
        const typedData: ICoverageOverrideFormData = {}

        // Helper function to treat empty strings and undefined values as null, but to treat 0 as a valid value
        function normalizedValue<T>(obj: T, key: keyof T) {
            const value = obj[key]

            return value === "" || typeof value === "undefined" ? null : value
        }

        // Helper function to check if a value is null or undefined
        function isNullOrUndefined(value: any): boolean {
            return typeof value === "undefined" || value === null
        }

        // Map over the data submitted to the override form
        Object.keys(data).forEach(key => {
            const typedKey = key as keyof ICoverageOverrideFormData

            // Value submitted in the form
            const valueNormalized = normalizedValue(data, typedKey)

            // Default values in the form
            const formValueNormalized = normalizedValue(
                defaultData.form,
                typedKey,
            )

            if (!isNullOrUndefined(defaultData.override[typedKey])) {
                // If the field has a non-null value in the existing override, always send the value submitted in the form to the server
                typedData[typedKey] = valueNormalized
            } else if (
                // toString() is used to compare moment objects
                formValueNormalized?.toString() !== valueNormalized?.toString()
            ) {
                // If the field doesn't have a value in the existing override, send it to the server if the entered value is different from the default value
                typedData[typedKey] = valueNormalized
            }
        })

        /*
         * There are a few special cases in which we want to send the value to the server even if it didn't meet the criteria to be sent specified above
         *
         * 1. If we're changing the financials, we want to override the benefit structure
         *    - Ideally the logic here would match the validation logic exactly
         *      - i.e. only require benefit structure if a previously-null copay or coinsurance is being set, or if a copay or coinsurance changes to or from 0
         *    - However, we're avoiding fixing for now
         * 2. If we're overriding the benefit structure, we will also override the financials
         *    - This probably isn't necessary, but we're avoiding fixing for now
         * 3. If we're overriding the third party administrator name, we always want to override the third party administrator type
         *    - The reason we don't do the reverse is because when name is overridden, payer_id will be set to null, which is not what we want when overriding type
         *
         * Since there is currently no special logic for these dependent fields, we've created a config and a loop to handle them.
         *
         */

        // Loop through the mapping...
        OVERRIDES_CONFIG.bulkOverrides.dependentFieldsMapping.forEach(item => {
            // For each item, if any of the fields has an override...
            const isOverride = item.fields.some(
                field => !isNullOrUndefined(typedData[field]),
            )

            // ...then override the dependent fields as well
            if (isOverride) {
                item.dependents.forEach(dependent => {
                    typedData[dependent] = normalizedValue(data, dependent)
                })
            }
        })

        return typedData
    }

    return {
        // vars
        isOverridesEnabled,
        suggestedOverrideExpirationDate,
        minOverrideExpirationDate,
        isCreateOverrideLoading,
        // state / form
        overridesFormKey,
        setOverridesFormKey,
        dataWatcher,
        overridesFormMethods,
        // methods
        getFormDefaultData,
        onUseSuggestedOverrideExpiration,
        onResetOverride,
        validateOverrideCreation,
        mapCreationFormData,
    }
}
